import { SERIALIZATION, DESERIALIZATION, INLINE_TYPES } from '../constants';

// inlineTypes controls the link and mentions marks, as they are exceptions
// and are handled differently from bold and italic
const inlineTypes = INLINE_TYPES;

const textSerializer = ({
  node,
  direction,
  serializeMark,
  extractChildren,
  matchText,
  createText,
}) => {
  if (matchText({ node })) {
    let children = extractChildren({ node });
    const normalizedChildren = (
      direction === SERIALIZATION
        ? normalizeDeserializedChildren
        : normalizeSerializedChildren
    )({
      children,
    });

    const marks = serializeMark(normalizedChildren);
    const transformedTextChildren = transformTextChildren({
      children,
      marks,
      direction,
    });

    return createText({
      children: transformedTextChildren,
    });
  }
};

// normalizes markups into an array including all needed informations for both directions
// [
//   { object: 'mark', type: 'plain', text: 'a ', start: 0, end: 2 },
//   { object: 'mark', type: 'bold', text: 'text ', start: 2, end: 6 },
//   { object: 'mark', type: 'italic', text: 'text ', start: 2, end: 6 },
// eslint-disable-next-line max-len
//   { object: 'mark', type: 'link', text: 'text ', start: 2, end: 6, data : { href : 'http://xing.com'} },
// ]
const normalizeDeserializedChildren = ({ children = [] }) => {
  let textIndex = 0;
  const normalizedChildren = children.flatMap((child) => {
    let childMarks = [];
    // Get true values from node and create childMarks array
    const childEntries = Object.entries(child);
    childEntries.forEach(([key, value]) => {
      if (value === true) {
        childMarks.push({
          type: key,
          data: child.data || {},
        });
      }
    });

    let normalizedMarks;
    if (childMarks.length > 0) {
      normalizedMarks = childMarks.map((mark) => {
        const { data } = mark;
        return {
          object: 'mark',
          type: mark.type,
          text: child.text,
          start: textIndex,
          end: textIndex + child.text.length,
          data,
        };
      });
    } else {
      normalizedMarks = {
        object: 'mark',
        type: 'plain',
        text: child.text,
        start: textIndex,
        end: textIndex + child.text.length,
      };
    }
    textIndex = textIndex + child.text.length;
    return normalizedMarks;
  });

  return normalizedChildren;
};

// normalizes markups into an array including all needed informations for both directions
// [
//   { __typename: 'ArticlePlainText', text: 'a ', start: 0, end: 2 },
//   { __typename: 'ArticleBoldMarkup', text: 'text ', start: 2, end: 6 },
//   { __typename: 'ArticleItalicMarkup', text: 'text ', start: 2, end: 6 },
//   { __typename: 'ArticleLinkMarkup', text: 'text ', start: 2, end: 6, href: 'http://xing.com'},
// ]
const normalizeSerializedChildren = ({ children: { text, markups = [] } }) => {
  markups = markups.map((m) => ({ ...m, text: text.slice(m.start, m.end) }));
  const markupsByChar = new Array(text.length).fill().map(() => []);
  markups.forEach(({ start, end, __typename }) => {
    for (var i = start; i < end; i++) {
      if (!markupsByChar[i].includes(__typename)) {
        markupsByChar[i].push(__typename);
      }
      markupsByChar[i].sort();
    }
  });

  let offset = null;
  markupsByChar.forEach((m, i) => {
    const isLastRun = i === text.length - 1;
    if (m.length === 0 && !isLastRun) {
      if (offset === null) {
        offset = i;
      }
    } else {
      if (offset !== null) {
        const endIndex = isLastRun ? i + 1 : i;
        markups.push({
          __typename: 'ArticlePlainText',
          start: offset,
          end: endIndex,
          text: text.slice(offset, endIndex),
        });
      }
      offset = null;
    }
  });
  markups.sort((a, b) => a.start - b.start);

  return markups;
};

// merges adjacent ranges of same mark and extracts top level text
const transformDeserializedTextChildren = ({ children, marks }) => {
  const adjacentMarks = marks.reduce((memo, mark) => {
    const prevMark = memo.find(
      (m) => m.__typename === mark.__typename && m.end === mark.start
    );
    if (!prevMark) {
      return memo.concat([mark]);
    }
    prevMark.end = mark.end;
    return memo;
  }, []);

  const formattedMarks = adjacentMarks.map((mark) => {
    const { __typename, ...attributes } = mark;
    return {
      [__typename]: {
        ...attributes,
      },
    };
  });

  return {
    text: children.reduce((t, { text }) => t + text, ''),
    marks: formattedMarks,
  };
};

// merges multiple marks into a common text array
const transformSerializedTextChildren = ({ children, marks }) => {
  const getMarkupsAtCurrentLength = (cl) =>
    marks.filter((m) => cl >= m.start && cl < m.end).sort();
  const getNonInlineMarksAtCurrentLength = (cl) =>
    marks
      .filter(
        (m) => cl >= m.start && cl < m.end && !inlineTypes.includes(m.type)
      )
      .sort();

  let childrenNodes = [];
  let str = '';
  let currentLength = 0;
  let currentMarks = getMarkupsAtCurrentLength(currentLength);
  let inlineMarks = marks.filter((mark) => inlineTypes.includes(mark.type));
  inlineMarks.map((inlineMark) => (inlineMark.children = []));

  // Handle common marks
  Array.from(children.text).forEach((char) => {
    currentLength = currentLength + char.length;
    const marksAtIndex = getMarkupsAtCurrentLength(currentLength);
    str = str + char;

    if (
      currentMarks.map((m) => m.type).join() !==
        marksAtIndex.map((m) => m.type).join() ||
      currentLength === children.text.length
    ) {
      let nodeAttributes = {};
      let pureNodeAttributes = {};
      let isInlineMark = false;
      currentMarks.forEach((mark) => {
        nodeAttributes = { ...nodeAttributes, ...mark };
      });

      // Handle inline Marks
      inlineMarks.forEach((inlineMark) => {
        if (
          inlineMark.start <= currentLength - 1 &&
          inlineMark.end >= currentLength
        ) {
          isInlineMark = true;
          let pureMarks = getNonInlineMarksAtCurrentLength(currentLength - 1);
          pureMarks.forEach((mark) => {
            pureNodeAttributes = { ...pureNodeAttributes, ...mark };
          });

          inlineMark.children.push({ text: str, ...pureNodeAttributes });
        }
        if (inlineMark.end === currentLength) {
          isInlineMark = true;
          childrenNodes.push({
            ...inlineMark,
          });
        }
      });

      if (isInlineMark === false) {
        childrenNodes.push({
          text: str,
          ...nodeAttributes,
        });
      }

      isInlineMark = false;
      str = '';
      currentMarks = marksAtIndex;
    }
  });

  // Clean node information
  childrenNodes.map((node) => {
    if (node.children) {
      node.children.map((nestedChild) => {
        deleteNodeStartEnd(nestedChild);
        deleteNonInlineNodeType(nestedChild);
      });
    }
    deleteNodeStartEnd(node);
    deleteNonInlineNodeType(node);
  });

  return childrenNodes;
};

const deleteNonInlineNodeType = (node) => {
  if (!inlineTypes.includes(node.type)) {
    delete node.type;
  }
  return node;
};

const deleteNodeStartEnd = (node) => {
  if (Object.prototype.hasOwnProperty.call(node, 'start')) {
    delete node.start;
  }
  if (Object.prototype.hasOwnProperty.call(node, 'end')) {
    delete node.end;
  }
  return node;
};

const transformTextChildren = ({ children, marks = [], direction }) => {
  if (direction === SERIALIZATION) {
    return transformDeserializedTextChildren({ children, marks });
  }
  if (direction === DESERIALIZATION) {
    return transformSerializedTextChildren({ children, marks });
  }
};

export { textSerializer, transformTextChildren };
